import { useLanguage } from "@/context/language"
import {
  buildIntegrationConnectUrl,
  createIntegrationConnectSession,
  coworkAgentMode,
  deleteIntegrationConnection,
  deleteFile,
  getFileContentBlob,
  listFiles,
  listIntegrationConnections,
  listWorkflows,
  listProjects,
  type CoworkFile,
  type CoworkWorkflow,
  type CoworkProject,
} from "@/cowork/api"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { Dialog, DialogBody, DialogHeader, DialogTitle, DialogTitleGroup } from "@opencode-ai/ui/v2/dialog-v2"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import { useNavigate } from "@solidjs/router"
import { createEffect, createMemo, createResource, For, onCleanup, onMount, Show, type JSX } from "solid-js"
import { createStore } from "solid-js/store"

type LibraryFile = CoworkFile & {
  projectName: string
}

type LibraryFileType = "all" | "images" | "documents" | "spreadsheets"
type LibraryFolder = {
  id: string
  name: string
  kind: "project" | "workflow"
  count: number
}

type CoworkIntegrationCatalogItem = {
  label: string
  logoPath: string
  description: keyof typeof import("@/i18n/en").dict
  canConnect: boolean
  providerKey?: string
}

type StoredAgent = {
  id: string
  name: string
  description?: string
  instructions?: string
  mode?: string
  isDefault?: boolean
  enabled?: boolean
  tools?: Record<string, boolean>
  integrations?: string[]
  permissions?: Record<string, string>
  updatedAt?: string
}

export function CoworkNavigationShell(props: { children: JSX.Element }) {
  const language = useLanguage()
  const dialog = useDialog()
  const navigate = useNavigate()
  const [store, setStore] = createStore({
    collapsed: storedNavigationCollapsed(),
    selectedProjectID: storedSelectedProject(),
    selectedWorkflowID: storedSelectedWorkflow(),
    width: storedNavigationWidth(),
    agentVersion: 0,
    search: "",
  })
  const [projects, projectActions] = createResource(() => (coworkAgentMode() ? true : undefined), () => listProjects())
  const selectedProject = createMemo(() => projects()?.find((project) => project.id === store.selectedProjectID))
  const [workflows, workflowActions] = createResource(() => selectedProject()?.id, listWorkflows)
  const searchQuery = createMemo(() => store.search.trim().toLowerCase())
  const filteredWorkflows = createMemo(() => {
    const query = searchQuery()
    const list = workflows() ?? []
    if (!query) return list
    return list.filter((workflow) => workflow.name.toLowerCase().includes(query))
  })
  const filteredProjects = createMemo(() => {
    const query = searchQuery()
    const list = projects() ?? []
    if (!query) return list
    return list.filter((project) => project.name.toLowerCase().includes(query) || (project.id === store.selectedProjectID && filteredWorkflows().length > 0))
  })
  const agents = createMemo(() => {
    store.agentVersion
    return loadAgents(store.selectedProjectID, language)
  })

  onMount(() => {
    const navigationListener = (event: Event) => {
      const detail = (event as CustomEvent<{ collapsed?: boolean }>).detail
      setStore("collapsed", detail?.collapsed ?? storedNavigationCollapsed())
    }
    const projectListener = (event: Event) => {
      const detail = (event as CustomEvent<{ projectID?: string; view?: string }>).detail
      if (!detail?.projectID) return
      localStorage.setItem("cowork.agent.selectedProjectId", detail.projectID)
      setStore("selectedProjectID", detail.projectID)
    }
    const agentsListener = () => setStore("agentVersion", (version) => version + 1)
    const workflowListener = (event: Event) => {
      const detail = (event as CustomEvent<{ workflowID?: string }>).detail
      if (detail?.workflowID) setStore("selectedWorkflowID", detail.workflowID)
    }
    window.addEventListener("cowork:navigation-toggle", navigationListener)
    window.addEventListener("cowork:navigation-select", projectListener)
    window.addEventListener("cowork:project-select", projectListener)
    window.addEventListener("cowork:agents-changed", agentsListener)
    window.addEventListener("cowork:workflow-select", workflowListener)
    window.addEventListener("cowork:workflows-changed", workflowActions.refetch)
    window.addEventListener("cowork:projects-changed", projectActions.refetch)
    onCleanup(() => {
      window.removeEventListener("cowork:navigation-toggle", navigationListener)
      window.removeEventListener("cowork:navigation-select", projectListener)
      window.removeEventListener("cowork:project-select", projectListener)
      window.removeEventListener("cowork:agents-changed", agentsListener)
      window.removeEventListener("cowork:workflow-select", workflowListener)
      window.removeEventListener("cowork:workflows-changed", workflowActions.refetch)
      window.removeEventListener("cowork:projects-changed", projectActions.refetch)
    })
  })

  createEffect(() => {
    const list = projects()
    if (!list) return
    if (projects.loading) return
    if (!store.selectedProjectID || list.some((project) => project.id === store.selectedProjectID)) return
    localStorage.removeItem("cowork.agent.selectedProjectId")
    setStore("selectedProjectID", "")
  })

  const selectProject = (project: CoworkProject) => {
    localStorage.setItem("cowork.agent.selectedProjectId", project.id)
    localStorage.setItem("cowork.agent.centralView", "project")
    localStorage.removeItem("cowork.agent.selectedAgent")
    setStore("selectedProjectID", project.id)
    void workflowActions.refetch()
    window.dispatchEvent(new CustomEvent("cowork:navigation-select", { detail: { view: "project", projectID: project.id } }))
    navigate("/")
    setTimeout(() => window.dispatchEvent(new CustomEvent("cowork:navigation-select", { detail: { view: "project", projectID: project.id } })))
  }

  const selectAgent = (agent: StoredAgent) => {
    localStorage.setItem("cowork.agent.centralView", "agent")
    localStorage.setItem("cowork.agent.selectedAgent", JSON.stringify(agent))
    window.dispatchEvent(new CustomEvent("cowork:navigation-select", { detail: { view: "agent", projectID: store.selectedProjectID, agent } }))
    navigate("/")
    setTimeout(() => window.dispatchEvent(new CustomEvent("cowork:navigation-select", { detail: { view: "agent", projectID: store.selectedProjectID, agent } })))
  }

  const addProject = () => {
    navigate("/")
    setTimeout(() => window.dispatchEvent(new CustomEvent("cowork:project-add")))
  }

  const addAgent = () => {
    const agent = draftAgent()
    localStorage.setItem("cowork.agent.centralView", "agent")
    localStorage.setItem("cowork.agent.selectedAgent", JSON.stringify(agent))
    navigate("/")
    setTimeout(() => window.dispatchEvent(new CustomEvent("cowork:navigation-select", { detail: { view: "agent", projectID: store.selectedProjectID, agent } })))
  }

  const openFiles = () => dialog.show(() => <CoworkFilesLibraryDialog projects={projects() ?? []} />)
  const openIntegrations = () => dialog.show(() => <CoworkIntegrationsDialog />)

  const selectWorkflow = (workflow: CoworkWorkflow) => {
    localStorage.setItem("cowork.agent.selectedWorkflowId", workflow.id)
    setStore("selectedWorkflowID", workflow.id)
    window.dispatchEvent(new CustomEvent("cowork:workflow-select", { detail: { projectID: store.selectedProjectID, workflowID: workflow.id } }))
    navigate("/")
  }

  const addWorkflow = () => {
    const projectID = store.selectedProjectID
    if (!projectID) return
    window.dispatchEvent(new CustomEvent("cowork:workflow-add", { detail: { projectID } }))
  }

  const deleteWorkflow = (workflow: CoworkWorkflow) => {
    window.dispatchEvent(new CustomEvent("cowork:workflow-delete", { detail: { projectID: store.selectedProjectID, workflowID: workflow.id } }))
    setTimeout(() => void workflowActions.refetch())
  }

  const navigationToggle = () => (
    <TooltipV2 placement={store.collapsed ? "right" : "bottom"} value={language.t(store.collapsed ? "cowork.navigation.expand" : "cowork.navigation.collapse")}>
      <IconButtonV2
        type="button"
        variant="ghost-muted"
        size="large"
        class="!h-9 !w-9 shrink-0 rounded-full border border-v2-border-border-base bg-v2-background-bg-layer-01"
        icon={<IconV2 name="sidebar-right" classList={{ "rotate-180": !store.collapsed }} />}
        onClick={toggleNavigation}
        aria-label={language.t(store.collapsed ? "cowork.navigation.expand" : "cowork.navigation.collapse")}
        title={language.t(store.collapsed ? "cowork.navigation.expand" : "cowork.navigation.collapse")}
      />
    </TooltipV2>
  )

  const projectAction = (type: "edit" | "delete", project: CoworkProject) => {
    navigate("/")
    setTimeout(() => window.dispatchEvent(new CustomEvent(`cowork:project-${type}`, { detail: { projectID: project.id } })))
  }

  const startResize = (event: PointerEvent) => {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = store.width
    const move = (moveEvent: PointerEvent) => {
      const next = Math.min(420, Math.max(240, startWidth + moveEvent.clientX - startX))
      setStore("width", next)
      localStorage.setItem("cowork.agent.navigationWidth", String(next))
    }
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  if (!coworkAgentMode()) return <>{props.children}</>

  return (
    <div class="flex min-h-0 min-w-0 flex-1 self-stretch overflow-hidden gap-0 p-2 pt-0">
      <aside
        class="relative my-2 min-h-0 shrink-0 overflow-hidden rounded-[10px] border border-v2-border-border-base bg-v2-background-bg-base shadow-[var(--v2-elevation-raised)] transition-[width] duration-150 ease-out"
        classList={{ "w-12": store.collapsed }}
        style={store.collapsed ? undefined : { width: `${store.width}px` }}
      >
        <Show
          when={!store.collapsed}
          fallback={
            <div class="flex h-full flex-col items-center gap-3 px-1.5 py-4">
              {navigationToggle()}
              <div class="h-px w-9 bg-v2-border-border-base" />
              <For each={projects() ?? []}>
                {(project) => (
                  <TooltipV2 placement="right" value={project.name}>
                    <button
                      type="button"
                      class="flex size-8 shrink-0 items-center justify-center rounded-lg border border-v2-border-border-base text-13-medium text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base"
                      classList={{ "bg-v2-background-bg-layer-03 text-v2-text-text-base": store.selectedProjectID === project.id }}
                      onClick={() => selectProject(project)}
                      aria-label={project.name}
                    >
                      {project.name.trim().charAt(0).toUpperCase() || "P"}
                    </button>
                  </TooltipV2>
                )}
              </For>
              <div class="h-px w-9 bg-v2-border-border-base" />
              <TooltipV2 placement="right" value={language.t("cowork.agents.title")}>
                <button type="button" class="flex size-8 shrink-0 items-center justify-center rounded-lg border border-v2-border-border-base text-13-medium text-v2-text-text-muted" onClick={toggleNavigation}>
                  A
                </button>
              </TooltipV2>
            </div>
          }
        >
          <div class="flex h-full min-h-0 flex-col gap-5 overflow-y-auto px-4 py-5 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
            <div class="flex h-9 shrink-0 items-center gap-2">
              <label class="relative min-w-0 flex-1">
                <input
                  type="search"
                  value={store.search}
                  onInput={(event) => setStore("search", event.currentTarget.value)}
                  placeholder={language.t("common.search.placeholder")}
                  aria-label={language.t("common.search.placeholder")}
                  class="h-9 w-full rounded-full border border-v2-border-border-base bg-v2-background-bg-layer-01 px-4 pr-10 text-14-regular text-v2-text-text-base outline-none placeholder:text-v2-text-text-muted focus:border-v2-border-border-muted"
                />
                <IconV2 name="magnifying-glass" size="large" class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-v2-text-text-muted" />
              </label>
              {navigationToggle()}
            </div>
            <div class="flex flex-col gap-1">
              <button type="button" class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" onClick={openFiles}>
                <IconV2 name="folder" size="normal" class="shrink-0 text-v2-text-text-muted" />
                <span>{language.t("cowork.navigation.files")}</span>
              </button>
              <button type="button" class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" onClick={openIntegrations}>
                <IconV2 name="settings-gear" size="normal" class="shrink-0 text-v2-text-text-muted" />
                <span>{language.t("cowork.integrations.title")}</span>
              </button>
            </div>
            <div>
              <div class="mb-3 mt-2 flex items-center justify-between">
                <h2 class="text-16-semibold text-v2-text-text-muted">{language.t("home.projects")}</h2>
                <div class="flex items-center gap-1">
                  <IconButtonV2 variant="ghost-muted" size="large" icon={<IconV2 name="plus" />} onClick={addProject} aria-label={language.t("home.project.add")} title={language.t("home.project.add")} />
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <For each={filteredProjects()}>
                  {(project) => (
                    <div>
                      <div class="group/project-row flex h-9 min-w-0 items-center rounded-md hover:bg-v2-overlay-simple-overlay-hover" classList={{ "bg-v2-background-bg-layer-03": store.selectedProjectID === project.id }}>
                        <button type="button" class="flex min-w-0 flex-1 items-center gap-2 px-2 text-left" onClick={() => selectProject(project)}>
                          <span class="flex size-6 shrink-0 items-center justify-center rounded-md bg-v2-background-bg-layer-03 text-12-medium text-v2-text-text-muted shadow-[inset_0_0_0_1px_var(--v2-border-border-muted)]">
                            {project.name.trim().charAt(0).toUpperCase() || "P"}
                          </span>
                          <span class="min-w-0 flex-1 truncate text-14-regular text-v2-text-text-base">{project.name}</span>
                        </button>
                        <Show when={store.selectedProjectID === project.id}>
                          <IconButtonV2 variant="ghost-muted" size="large" icon={<IconV2 name="plus" />} onClick={addWorkflow} aria-label={language.t("cowork.workflow.create")} title={language.t("cowork.workflow.create")} />
                        </Show>
                        <MenuV2>
                          <MenuV2.Trigger>
                            <button type="button" class="mr-1 rounded px-1.5 py-1 text-v2-text-text-muted opacity-0 hover:bg-v2-background-bg-layer-02 group-hover/project-row:opacity-100">
                              ...
                            </button>
                          </MenuV2.Trigger>
                          <MenuV2.Content fit>
                            <MenuV2.Item onSelect={() => projectAction("edit", project)}>{language.t("dialog.project.edit.title")}</MenuV2.Item>
                            <MenuV2.Item onSelect={() => projectAction("delete", project)}>{language.t("common.delete")}</MenuV2.Item>
                          </MenuV2.Content>
                        </MenuV2>
                      </div>
                      <Show when={store.selectedProjectID === project.id}>
                        <div class="ml-8 mt-1 flex flex-col gap-1">
                          <For each={filteredWorkflows()} fallback={<div class="px-2 py-1 text-12-regular text-v2-text-text-muted">{language.t("cowork.workflow.empty")}</div>}>
                            {(workflow) => (
                    <div
                      class="group/workflow-row flex h-7 min-w-0 items-center rounded-md text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base"
                      classList={{ "bg-v2-background-bg-layer-03 text-v2-text-text-base": store.selectedWorkflowID === workflow.id }}
                    >
                      <button type="button" class="min-w-0 flex-1 px-2 text-left text-12-regular" onClick={() => selectWorkflow(workflow)}>
                        <span class="block min-w-0 truncate">{workflow.name}</span>
                      </button>
                      <MenuV2>
                        <MenuV2.Trigger>
                          <button type="button" class="mr-1 rounded px-1.5 py-0.5 text-v2-text-text-muted opacity-0 hover:bg-v2-background-bg-layer-02 group-hover/workflow-row:opacity-100">
                            ...
                          </button>
                        </MenuV2.Trigger>
                        <MenuV2.Content fit>
                          <MenuV2.Item onSelect={() => deleteWorkflow(workflow)}>{language.t("common.delete")}</MenuV2.Item>
                        </MenuV2.Content>
                      </MenuV2>
                    </div>
                  )}
                          </For>
                        </div>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
            </div>
            <div class="border-t border-v2-border-border-base pt-5">
              <div class="mb-3 flex items-center justify-between">
                <h2 class="text-16-semibold text-v2-text-text-muted">{language.t("cowork.agents.title")}</h2>
                <div class="flex min-w-0 items-center gap-1">
                  <IconButtonV2 variant="ghost-muted" size="large" icon={<IconV2 name="plus" />} disabled aria-label={language.t("cowork.agents.add")} />
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <For each={agents()} fallback={<div class="px-2 py-2 text-12-regular text-v2-text-text-muted">{language.t("cowork.agents.emptyCompact")}</div>}>
                  {(agent) => (
                    <button type="button" class="flex h-9 min-w-0 items-center gap-2 rounded-md px-2 text-left hover:bg-v2-overlay-simple-overlay-hover" onClick={() => selectAgent(agent)}>
                      <span class="flex size-6 shrink-0 items-center justify-center rounded-md bg-v2-background-bg-layer-03 text-12-medium text-v2-text-text-muted shadow-[inset_0_0_0_1px_var(--v2-border-border-muted)]">
                        {agent.name.trim().charAt(0).toUpperCase() || "A"}
                      </span>
                      <span class="min-w-0 flex-1 truncate text-14-regular text-v2-text-text-base">{agent.name}</span>
                      <Show when={agent.isDefault}>
                        <span class="rounded-full bg-blue-500/10 px-1.5 py-0.5 text-[10px] text-blue-300">{language.t("cowork.agents.defaultShort")}</span>
                      </Show>
                    </button>
                  )}
                </For>
              </div>
            </div>
          </div>
        </Show>
        <Show when={!store.collapsed}>
          <div
            class="absolute right-0 top-0 h-full w-2 cursor-col-resize bg-transparent hover:bg-v2-border-border-base/50"
            onPointerDown={startResize}
            aria-label={language.t("cowork.navigation.resize")}
            role="separator"
          />
        </Show>
      </aside>
      <div class="flex min-w-0 flex-1 overflow-hidden">{props.children}</div>
    </div>
  )
}

function CoworkFilesLibraryDialog(props: { projects: CoworkProject[] }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ search: "", type: "all" as LibraryFileType, projectID: "", workflowID: "" })
  const selectedProject = createMemo(() => props.projects.find((project) => project.id === store.projectID))
  const [projectWorkflows] = createResource(() => store.projectID || undefined, listWorkflows)
  const [files, filesActions] = createResource(
    () => props.projects,
    async (projects) =>
      (
        await Promise.all(
          projects.map((project) =>
            listFiles(project.id)
              .then((files) => files.map((file) => ({ ...file, projectName: project.name })))
              .catch(() => [] as LibraryFile[]),
          ),
        )
      )
        .flat()
        .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at)),
  )
  const filteredFiles = createMemo(() => {
    const query = store.search.trim().toLowerCase()
    return (files() ?? []).filter((file) => {
      const matchesSearch = !query || `${file.filename} ${file.projectName} ${file.content_type}`.toLowerCase().includes(query)
      return matchesSearch && fileTypeMatches(file, store.type)
    })
  })
  const scopedFiles = createMemo(() => filteredFiles().filter((file) => (!store.projectID || file.project_id === store.projectID) && (!store.workflowID || (store.workflowID === "__none__" ? !file.workflow_id : file.workflow_id === store.workflowID))))
  const projectFolders = createMemo<LibraryFolder[]>(() => props.projects.flatMap((project) => {
    const count = filteredFiles().filter((file) => file.project_id === project.id).length
    return count > 0 ? [{ id: project.id, name: project.name, kind: "project", count }] : []
  }))
  const workflowFolders = createMemo<LibraryFolder[]>(() => {
    const projectID = store.projectID
    if (!projectID) return []
    const projectFiles = filteredFiles().filter((file) => file.project_id === projectID)
    const folders = (projectWorkflows() ?? []).flatMap((workflow) => {
      const count = projectFiles.filter((file) => file.workflow_id === workflow.id).length
      return count > 0 ? [{ id: workflow.id, name: workflow.name, kind: "workflow" as const, count }] : []
    })
    const unassignedCount = projectFiles.filter((file) => !file.workflow_id).length
    return unassignedCount > 0 ? [...folders, { id: "__none__", name: language.t("cowork.files.library.noWorkflow"), kind: "workflow", count: unassignedCount }] : folders
  })
  const showFlatSearch = createMemo(() => store.search.trim().length > 0)
  const visibleFolders = createMemo(() => {
    if (showFlatSearch()) return []
    if (!store.projectID) return projectFolders()
    if (!store.workflowID) return workflowFolders()
    return []
  })
  const visibleFiles = createMemo(() => showFlatSearch() ? filteredFiles() : store.projectID && store.workflowID ? scopedFiles() : [])
  const openFolder = (folder: LibraryFolder) => {
    if (folder.kind === "project") {
      setStore({ projectID: folder.id, workflowID: "" })
      return
    }
    setStore("workflowID", folder.id)
  }
  const resetPath = () => setStore({ projectID: "", workflowID: "" })
  const selectedWorkflowName = createMemo(() => store.workflowID === "__none__" ? language.t("cowork.files.library.noWorkflow") : (projectWorkflows() ?? []).find((workflow) => workflow.id === store.workflowID)?.name ?? "")
  const download = (file: LibraryFile) => {
    void getFileContentBlob(file.project_id, file.id).then((blob) => {
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = file.filename
      anchor.click()
      URL.revokeObjectURL(url)
    })
  }
  const remove = (file: LibraryFile) => {
    void deleteFile(file.project_id, file.id).then(() => filesActions.refetch())
  }

  return (
    <Dialog size="x-large" fit class="h-[min(calc(100vh-48px),760px)] w-[min(calc(100vw-48px),1080px)] overflow-hidden">
      <DialogHeader>
        <DialogTitleGroup title={language.t("cowork.navigation.files")} description={language.t("cowork.files.library.description")} />
      </DialogHeader>
      <DialogBody class="min-h-0 overflow-hidden">
        <div class="flex h-full min-h-0 flex-col">
          <div class="flex flex-col gap-3 border-b border-v2-border-border-base px-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <label class="relative min-w-0 sm:w-[360px]">
              <input
                type="search"
                value={store.search}
                onInput={(event) => setStore("search", event.currentTarget.value)}
                placeholder={language.t("common.search.placeholder")}
                aria-label={language.t("common.search.placeholder")}
                class="h-9 w-full rounded-full border border-v2-border-border-base bg-v2-background-bg-layer-01 px-4 pr-10 text-14-regular text-v2-text-text-base outline-none placeholder:text-v2-text-text-muted focus:border-v2-border-border-muted"
              />
              <IconV2 name="magnifying-glass" size="large" class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-v2-text-text-muted" />
            </label>
            <div class="flex shrink-0 flex-wrap gap-2">
              <For each={libraryFileTypes}>
                {(type) => (
                  <button
                    type="button"
                    class="rounded-full px-3 py-1.5 text-12-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                    classList={{ "bg-v2-background-bg-layer-03 text-v2-text-text-base": store.type === type }}
                    onClick={() => setStore("type", type)}
                  >
                    {language.t(libraryFileTypeLabel(type))}
                  </button>
                )}
              </For>
            </div>
          </div>
          <Show when={!showFlatSearch()}>
            <div class="flex items-center gap-2 border-b border-v2-border-border-base px-3 py-2 text-12-regular text-v2-text-text-muted">
              <button type="button" class="hover:text-v2-text-text-base" onClick={resetPath}>{language.t("cowork.files.library.allProjects")}</button>
              <Show when={selectedProject()}>
                {(project) => (
                  <>
                    <span>/</span>
                    <button type="button" class="max-w-60 truncate hover:text-v2-text-text-base" onClick={() => setStore("workflowID", "")}>{project().name}</button>
                  </>
                )}
              </Show>
              <Show when={selectedWorkflowName()}>
                {(name) => (
                  <>
                    <span>/</span>
                    <span class="max-w-60 truncate text-v2-text-text-base">{name()}</span>
                  </>
                )}
              </Show>
            </div>
          </Show>
          <div class="grid grid-cols-[minmax(0,1fr)_180px_150px_110px_44px] gap-3 border-b border-v2-border-border-base px-3 py-2 text-12-medium text-v2-text-text-muted">
            <div>{language.t("cowork.files.library.name")}</div>
            <div>{language.t("cowork.files.library.project")}</div>
            <div>{language.t("cowork.files.library.modified")}</div>
            <div>{language.t("cowork.files.library.size")}</div>
            <div />
          </div>
          <div class="min-h-0 flex-1 overflow-y-auto [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-width:thin]">
            <Show when={!files.loading} fallback={<div class="px-3 py-8 text-center text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</div>}>
              <For each={visibleFolders()}>
                {(folder) => (
                  <button type="button" class="grid w-full grid-cols-[minmax(0,1fr)_180px_150px_110px_44px] items-center gap-3 border-b border-v2-border-border-base/60 px-3 py-3 text-left text-13-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" onClick={() => openFolder(folder)}>
                    <div class="flex min-w-0 items-center gap-3">
                      <span class="flex size-8 shrink-0 items-center justify-center rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-02">
                        <IconV2 name="folder" size="normal" class="text-v2-text-text-muted" />
                      </span>
                      <span class="truncate">{folder.name}</span>
                    </div>
                    <div class="truncate text-v2-text-text-muted">{folder.kind === "project" ? language.t("cowork.files.library.project") : language.t("cowork.workflow.label")}</div>
                    <div class="truncate text-v2-text-text-muted" />
                    <div class="truncate text-v2-text-text-muted">{folder.count}</div>
                    <div class="flex justify-end text-v2-text-text-muted"><IconV2 name="chevron-right" size="small" /></div>
                  </button>
                )}
              </For>
              <For each={visibleFiles()} fallback={<Show when={visibleFolders().length === 0}><div class="px-3 py-8 text-center text-13-regular text-v2-text-text-muted">{language.t("cowork.files.library.empty")}</div></Show>}>
                {(file) => (
                  <div class="grid grid-cols-[minmax(0,1fr)_180px_150px_110px_44px] items-center gap-3 border-b border-v2-border-border-base/60 px-3 py-3 text-13-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover">
                    <div class="flex min-w-0 items-center gap-3">
                      <span class="flex size-8 shrink-0 items-center justify-center rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-02">
                        <FileIcon node={{ path: file.filename, type: "file" }} class="size-4 shrink-0" />
                      </span>
                      <span class="truncate">{file.filename}</span>
                    </div>
                    <div class="truncate text-v2-text-text-muted">{file.projectName}</div>
                    <div class="truncate text-v2-text-text-muted">{formatLibraryDate(file.updated_at)}</div>
                    <div class="truncate text-v2-text-text-muted">{formatLibrarySize(file.size)}</div>
                    <MenuV2 gutter={4} placement="bottom-end">
                      <MenuV2.Trigger>
                        <button type="button" class="flex size-8 items-center justify-center rounded-md text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base" aria-label={language.t("common.moreOptions")}>
                          <IconV2 name="outline-dots" size="normal" />
                        </button>
                      </MenuV2.Trigger>
                      <MenuV2.Portal>
                        <MenuV2.Content>
                          <MenuV2.Item onSelect={() => download(file)}>
                            <span class="flex items-center gap-2"><IconV2 name="download" size="small" />{language.t("cowork.files.panel.download")}</span>
                          </MenuV2.Item>
                          <MenuV2.Item onSelect={() => remove(file)}>
                            <span class="flex items-center gap-2 text-red-300"><IconV2 name="trash" size="small" />{language.t("common.delete")}</span>
                          </MenuV2.Item>
                        </MenuV2.Content>
                      </MenuV2.Portal>
                    </MenuV2>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  )
}

function CoworkIntegrationsDialog() {
  const language = useLanguage()
  const [store, setStore] = createStore({ search: "" })
  const [connections, connectionActions] = createResource(() => listIntegrationConnections())
  onMount(() => {
    const refresh = () => void connectionActions.refetch()
    const visibilityRefresh = () => {
      if (document.visibilityState === "visible") refresh()
    }
    window.addEventListener("focus", refresh)
    document.addEventListener("visibilitychange", visibilityRefresh)
    onCleanup(() => {
      window.removeEventListener("focus", refresh)
      document.removeEventListener("visibilitychange", visibilityRefresh)
    })
  })
  const filteredIntegrations = createMemo(() => {
    const query = store.search.trim().toLowerCase()
    if (!query) return coworkIntegrationCatalog
    return coworkIntegrationCatalog.filter((integration) => `${integration.label} ${language.t(integration.description, { name: integration.label })}`.toLowerCase().includes(query))
  })
  const connect = (providerKey: string) => {
    const popup = window.open("about:blank", "_blank")
    if (popup) popup.opener = null
    void createIntegrationConnectSession(providerKey).then((session) => {
      const url = buildIntegrationConnectUrl(session)
      if (popup) popup.location.href = url
      if (!popup) window.location.href = url
      setTimeout(() => void connectionActions.refetch(), 1500)
      setTimeout(() => void connectionActions.refetch(), 5000)
    }).catch(() => popup?.close())
  }
  const connectionForProvider = (providerKey: string) => (connections()?.connections ?? []).find((connection) => integrationConnectionProviderKey(connection) === providerKey)
  const disconnect = (providerKey: string, connectionID: string) => {
    void deleteIntegrationConnection(providerKey, connectionID).then(() => connectionActions.refetch())
  }
  return (
    <Dialog fit containerClass="!h-auto max-h-[calc(100vh_-_16px)] !w-[min(calc(100vw_-_16px),900px)]">
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.integrations.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="min-h-0 flex-none gap-0 p-4">
        <p class="mb-4 text-13-regular text-v2-text-text-muted">{language.t("cowork.integrations.description")}</p>
        <div class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label class="relative block min-w-0 sm:w-[420px]">
            <input
              type="search"
              value={store.search}
              onInput={(event) => setStore("search", event.currentTarget.value)}
              placeholder={language.t("common.search.placeholder")}
              aria-label={language.t("common.search.placeholder")}
              class="h-9 w-full rounded-full border border-v2-border-border-base bg-v2-background-bg-layer-01 px-4 pr-10 text-14-regular text-v2-text-text-base outline-none placeholder:text-v2-text-text-muted focus:border-v2-border-border-muted"
            />
            <IconV2 name="magnifying-glass" size="large" class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-v2-text-text-muted" />
          </label>
          <TooltipV2 placement="bottom" value={language.t("cowork.integrations.refresh")}>
            <IconButtonV2
              type="button"
              variant="ghost-muted"
              size="large"
              icon={<IconV2 name="refresh" classList={{ "animate-spin": connections.loading }} />}
              disabled={connections.loading}
              onClick={() => void connectionActions.refetch()}
              aria-label={language.t("cowork.integrations.refresh")}
              title={language.t("cowork.integrations.refresh")}
            />
          </TooltipV2>
        </div>
        <div class="h-[500px] overflow-y-scroll pr-2 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
          <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <For each={filteredIntegrations()} fallback={<div class="col-span-full py-8 text-center text-13-regular text-v2-text-text-muted">{language.t("cowork.integrations.empty")}</div>}>
              {(integration) => (
                <div class="flex min-h-[148px] flex-col rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
                  <div class="flex items-start gap-3">
                    <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white p-2">
                      <img src={integration.logoPath} alt="" class="max-h-full max-w-full" />
                    </span>
                    <div class="min-w-0 flex-1">
                      <div class="truncate text-14-medium text-v2-text-text-base">{integration.label}</div>
                    <div class="mt-1 text-12-regular text-v2-text-text-muted">
                        {integration.providerKey && connectionForProvider(integration.providerKey) ? language.t("cowork.integrations.connected") : integration.canConnect ? language.t("cowork.integrations.available") : language.t("cowork.integrations.planned")}
                      </div>
                    </div>
                  </div>
                  <p class="mt-3 line-clamp-3 text-12-regular leading-5 text-v2-text-text-muted">{language.t(integration.description, { name: integration.label })}</p>
                  <button
                    type="button"
                    disabled={!integration.providerKey}
                    class="mt-auto h-8 rounded-md border border-v2-border-border-base px-3 text-12-medium text-v2-text-text-base hover:bg-v2-background-bg-layer-02 disabled:text-v2-text-text-muted disabled:opacity-60"
                    onClick={() => {
                      if (!integration.providerKey) return
                      const connection = connectionForProvider(integration.providerKey)
                      if (connection) return disconnect(integration.providerKey, connection.connection_id)
                      connect(integration.providerKey)
                    }}
                  >
                    {integration.providerKey && connectionForProvider(integration.providerKey) ? language.t("cowork.integrations.disconnect") : language.t("cowork.integrations.connect")}
                  </button>
                </div>
              )}
            </For>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  )
}

const coworkIntegrationCatalog: readonly CoworkIntegrationCatalogItem[] = [
  { label: "Google Drive", logoPath: "/template-logos/google-drive.svg", description: "cowork.integrations.description.google-drive", canConnect: true, providerKey: "google-drive" },
  { label: "OneDrive", logoPath: "/template-logos/one-drive.svg", description: "cowork.integrations.description.one-drive", canConnect: true, providerKey: "one-drive" },
  { label: "SharePoint", logoPath: "/template-logos/sharepoint-online.svg", description: "cowork.integrations.description.sharepoint-online", canConnect: true, providerKey: "sharepoint-online" },
  { label: "Dropbox", logoPath: "/template-logos/dropbox.svg", description: "cowork.integrations.description.dropbox", canConnect: false },
  { label: "Box", logoPath: "/template-logos/box.svg", description: "cowork.integrations.description.box", canConnect: false },
  { label: "Notion", logoPath: "/template-logos/notion.svg", description: "cowork.integrations.description.notion", canConnect: false },
  { label: "Confluence", logoPath: "/template-logos/confluence.svg", description: "cowork.integrations.description.confluence", canConnect: false },
  { label: "Jira", logoPath: "/template-logos/jira.svg", description: "cowork.integrations.description.jira", canConnect: false },
  { label: "GitHub", logoPath: "/template-logos/github.svg", description: "cowork.integrations.description.github", canConnect: false },
  { label: "GitLab", logoPath: "/template-logos/gitlab.svg", description: "cowork.integrations.description.gitlab", canConnect: false },
  { label: "Slack", logoPath: "/template-logos/slack.svg", description: "cowork.integrations.description.slack", canConnect: false },
  { label: "Microsoft Teams", logoPath: "/template-logos/microsoft-teams.svg", description: "cowork.integrations.description.microsoft-teams", canConnect: false },
  { label: "Amazon S3", logoPath: "/template-logos/aws.svg", description: "cowork.integrations.description.aws", canConnect: false },
  { label: "Google Cloud", logoPath: "/template-logos/google-cloud-storage.svg", description: "cowork.integrations.description.google-cloud-storage", canConnect: false },
  { label: "Azure Blob Storage", logoPath: "/template-logos/azure-blob-storage.svg", description: "cowork.integrations.description.azure-blob-storage", canConnect: false },
  { label: "Figma", logoPath: "/template-logos/figma.svg", description: "cowork.integrations.description.figma", canConnect: false },
  { label: "Airtable", logoPath: "/template-logos/airtable.svg", description: "cowork.integrations.description.airtable", canConnect: false },
  { label: "Salesforce", logoPath: "/template-logos/salesforce.svg", description: "cowork.integrations.description.salesforce", canConnect: false },
  { label: "HubSpot", logoPath: "/template-logos/hubspot.svg", description: "cowork.integrations.description.hubspot", canConnect: false },
  { label: "Asana", logoPath: "/template-logos/asana.svg", description: "cowork.integrations.description.asana", canConnect: false },
  { label: "Linear", logoPath: "/template-logos/linear.svg", description: "cowork.integrations.description.linear", canConnect: false },
  { label: "Trello", logoPath: "/template-logos/trello.svg", description: "cowork.integrations.description.trello", canConnect: false },
  { label: "Zendesk", logoPath: "/template-logos/zendesk.svg", description: "cowork.integrations.description.zendesk", canConnect: false },
  { label: "Intercom", logoPath: "/template-logos/intercom.svg", description: "cowork.integrations.description.intercom", canConnect: false },
  { label: "Webflow", logoPath: "/template-logos/webflow.svg", description: "cowork.integrations.description.webflow", canConnect: false },
  { label: "WordPress", logoPath: "/template-logos/wordpress.svg", description: "cowork.integrations.description.wordpress", canConnect: false },
  { label: "1Password", logoPath: "/template-logos/1password.svg", description: "cowork.integrations.description.iam", canConnect: false },
  { label: "ActiveCampaign", logoPath: "/template-logos/active-campaign.svg", description: "cowork.integrations.description.marketing", canConnect: false },
  { label: "Adobe", logoPath: "/template-logos/adobe.svg", description: "cowork.integrations.description.productivity", canConnect: false },
  { label: "ADP", logoPath: "/template-logos/adp.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "Affinity", logoPath: "/template-logos/affinity.svg", description: "cowork.integrations.description.crmGeneric", canConnect: false },
  { label: "Apollo", logoPath: "/template-logos/apollo.svg", description: "cowork.integrations.description.marketing", canConnect: false },
  { label: "Attio", logoPath: "/template-logos/attio.svg", description: "cowork.integrations.description.crmGeneric", canConnect: false },
  { label: "AutoTask", logoPath: "/template-logos/autotask.svg", description: "cowork.integrations.description.ticketing", canConnect: false },
  { label: "Azure DevOps", logoPath: "/template-logos/azure-devops.svg", description: "cowork.integrations.description.devtools", canConnect: false },
  { label: "BambooHR", logoPath: "/template-logos/bamboohr.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "Basecamp", logoPath: "/template-logos/basecamp.svg", description: "cowork.integrations.description.productivity", canConnect: false },
  { label: "Bitbucket", logoPath: "/template-logos/bitbucket.svg", description: "cowork.integrations.description.devtools", canConnect: false },
  { label: "Braze", logoPath: "/template-logos/braze.svg", description: "cowork.integrations.description.marketing", canConnect: false },
  { label: "Calendly", logoPath: "/template-logos/calendly.svg", description: "cowork.integrations.description.productivity", canConnect: false },
  { label: "Canvas LMS", logoPath: "/template-logos/canvas-lms.svg", description: "cowork.integrations.description.knowledge", canConnect: false },
  { label: "ClickUp", logoPath: "/template-logos/clickup.svg", description: "cowork.integrations.description.productivity", canConnect: false },
  { label: "Contentful", logoPath: "/template-logos/contentful.svg", description: "cowork.integrations.description.content", canConnect: false },
  { label: "Copper", logoPath: "/template-logos/copper.svg", description: "cowork.integrations.description.crmGeneric", canConnect: false },
  { label: "DocuSign", logoPath: "/template-logos/docusign.svg", description: "cowork.integrations.description.documents", canConnect: false },
  { label: "Drata", logoPath: "/template-logos/drata.svg", description: "cowork.integrations.description.compliance", canConnect: false },
  { label: "Freshdesk", logoPath: "/template-logos/freshdesk.svg", description: "cowork.integrations.description.ticketing", canConnect: false },
  { label: "Gong", logoPath: "/template-logos/gong.svg", description: "cowork.integrations.description.sales", canConnect: false },
  { label: "Google Ads", logoPath: "/template-logos/google-ads.svg", description: "cowork.integrations.description.marketing", canConnect: false },
  { label: "Greenhouse", logoPath: "/template-logos/greenhouse.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "Gusto", logoPath: "/template-logos/gusto.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "Ironclad", logoPath: "/template-logos/ironclad.svg", description: "cowork.integrations.description.documents", canConnect: false },
  { label: "Klaviyo", logoPath: "/template-logos/klaviyo.svg", description: "cowork.integrations.description.marketing", canConnect: false },
  { label: "Lever", logoPath: "/template-logos/lever.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "Looker", logoPath: "/template-logos/looker.svg", description: "cowork.integrations.description.analytics", canConnect: false },
  { label: "Mailchimp", logoPath: "/template-logos/mailchimp.svg", description: "cowork.integrations.description.marketing", canConnect: false },
  { label: "Mailgun", logoPath: "/template-logos/mailgun.svg", description: "cowork.integrations.description.communication", canConnect: false },
  { label: "Marketo", logoPath: "/template-logos/marketo.svg", description: "cowork.integrations.description.marketing", canConnect: false },
  { label: "Miro", logoPath: "/template-logos/miro.svg", description: "cowork.integrations.description.productivity", canConnect: false },
  { label: "Mixpanel", logoPath: "/template-logos/mixpanel.svg", description: "cowork.integrations.description.analytics", canConnect: false },
  { label: "Monday.com", logoPath: "/template-logos/monday.svg", description: "cowork.integrations.description.productivity", canConnect: false },
  { label: "OpenAI", logoPath: "/template-logos/openai.svg", description: "cowork.integrations.description.ai", canConnect: false },
  { label: "Paychex", logoPath: "/template-logos/paychex.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "Personio", logoPath: "/template-logos/personio.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "QuickBooks", logoPath: "/template-logos/quickbooks.svg", description: "cowork.integrations.description.accounting", canConnect: false },
  { label: "Sage Intacct", logoPath: "/template-logos/sage-intacct.svg", description: "cowork.integrations.description.accounting", canConnect: false },
  { label: "ServiceNow", logoPath: "/template-logos/servicenow.svg", description: "cowork.integrations.description.ticketing", canConnect: false },
  { label: "Shopify", logoPath: "/template-logos/shopify.svg", description: "cowork.integrations.description.ecommerce", canConnect: false },
  { label: "Stripe", logoPath: "/template-logos/stripe.svg", description: "cowork.integrations.description.payments", canConnect: false },
  { label: "Workday", logoPath: "/template-logos/workday.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "Workable", logoPath: "/template-logos/workable.svg", description: "cowork.integrations.description.hr", canConnect: false },
  { label: "Xero", logoPath: "/template-logos/xero.svg", description: "cowork.integrations.description.accounting", canConnect: false },
  { label: "Zoho CRM", logoPath: "/template-logos/zoho-crm.svg", description: "cowork.integrations.description.crmGeneric", canConnect: false },
  { label: "Zoom", logoPath: "/template-logos/zoom.svg", description: "cowork.integrations.description.communication", canConnect: false },
] as const

const libraryFileTypes = ["all", "images", "documents", "spreadsheets"] as const

function libraryFileTypeLabel(type: LibraryFileType) {
  return `cowork.files.library.filter.${type}`
}

function fileTypeMatches(file: LibraryFile, type: LibraryFileType) {
  if (type === "all") return true
  if (type === "images") return file.content_type.startsWith("image/")
  if (type === "spreadsheets") return /spreadsheet|csv|excel/i.test(file.content_type) || /\.(xlsx|xls|csv)$/i.test(file.filename)
  return /pdf|wordprocessing|text|json|xml/i.test(file.content_type) || /\.(pdf|doc|docx|txt|json|xml)$/i.test(file.filename)
}

function formatLibraryDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function formatLibrarySize(bytes: number) {
  if (bytes < 1024) return new Intl.NumberFormat(undefined, { style: "unit", unit: "byte", unitDisplay: "short" }).format(bytes)
  if (bytes < 1024 * 1024) return new Intl.NumberFormat(undefined, { style: "unit", unit: "kilobyte", unitDisplay: "short", maximumFractionDigits: 1 }).format(bytes / 1024)
  return new Intl.NumberFormat(undefined, { style: "unit", unit: "megabyte", unitDisplay: "short", maximumFractionDigits: 1 }).format(bytes / 1024 / 1024)
}

function integrationConnectionProviderKey(connection: { provider_config_key?: string | null; provider_key?: string | null; provider?: string | null }) {
  return normalizedIntegrationProviderKey(connection.provider_key || connection.provider || connection.provider_config_key)
}

function normalizedIntegrationProviderKey(providerKey: string | null | undefined) {
  const normalized = providerKey?.trim().toLowerCase()
  if (normalized === "sharepoint") return "sharepoint-online"
  if (normalized === "gdrive") return "google-drive"
  if (normalized === "onedrive") return "one-drive"
  return normalized
}

function toggleNavigation() {
  const key = "cowork.agent.navigationCollapsed"
  const next = localStorage.getItem(key) !== "true"
  localStorage.setItem(key, String(next))
  window.dispatchEvent(new CustomEvent("cowork:navigation-toggle", { detail: { collapsed: next } }))
}

function storedNavigationCollapsed() {
  try {
    return localStorage.getItem("cowork.agent.navigationCollapsed") === "true"
  } catch {
    return false
  }
}

function storedNavigationWidth() {
  try {
    const value = Number(localStorage.getItem("cowork.agent.navigationWidth"))
    return Number.isFinite(value) ? Math.min(420, Math.max(240, value)) : 288
  } catch {
    return 288
  }
}

function storedSelectedProject() {
  try {
    return localStorage.getItem("cowork.agent.selectedProjectId") || ""
  } catch {
    return ""
  }
}

function storedSelectedWorkflow() {
  try {
    return localStorage.getItem("cowork.agent.selectedWorkflowId") || ""
  } catch {
    return ""
  }
}

function loadAgents(projectID: string, language: ReturnType<typeof useLanguage>): StoredAgent[] {
  if (!projectID) return []
  try {
    const stored = JSON.parse(localStorage.getItem(`cowork.agent.projectAgents.${projectID}`) || "[]") as StoredAgent[]
    return [...seedAgents(language), ...stored.filter((agent) => !builtInAgentIDs().includes(agent.id))]
  } catch {
    return seedAgents(language)
  }
}

function builtInAgentIDs() {
  return ["custom-report", "accounting"]
}

function seedAgents(language: ReturnType<typeof useLanguage>): StoredAgent[] {
  return [
    {
      id: "custom-report",
      name: language.t("cowork.agents.template.customReport"),
      description: language.t("cowork.agents.template.customReport.description"),
      instructions: language.t("cowork.agents.template.customReport.instructions"),
      mode: "primary",
      isDefault: true,
      enabled: true,
      tools: { readFiles: true, generateReports: true, webSearch: false, approvalWorkflow: true, integrations: true, calculations: true },
      integrations: ["google-drive", "one-drive", "sharepoint-online"],
      permissions: { read: "allow", edit: "ask", websearch: "deny", webfetch: "ask", bash: "deny", skill: "allow" },
    },
    {
      id: "accounting",
      name: language.t("cowork.agents.template.accounting"),
      description: language.t("cowork.agents.template.accounting.description"),
      instructions: language.t("cowork.agents.template.accounting.instructions"),
      mode: "primary",
      isDefault: false,
      enabled: true,
      tools: { readFiles: true, generateReports: true, webSearch: false, approvalWorkflow: true, integrations: false, calculations: true },
      integrations: [],
      permissions: { read: "allow", edit: "ask", websearch: "deny", webfetch: "ask", bash: "ask", skill: "allow" },
    },
  ]
}

function draftAgent(): StoredAgent {
  return {
    id: `draft-${Date.now()}`,
    name: "New Agent",
    description: "Configure this agent before using it in a workflow.",
    instructions: "Describe what this agent should do, when it should be used, and which outputs it should produce.",
    mode: "primary",
    enabled: true,
    tools: { readFiles: true, generateReports: false, webSearch: false, approvalWorkflow: false, integrations: false, calculations: true },
    integrations: [],
    permissions: { read: "allow", edit: "ask", websearch: "deny", webfetch: "ask", bash: "deny", skill: "allow" },
  }
}
